import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../../firebase/config";
import { doc, updateDoc, onSnapshot, arrayUnion } from "firebase/firestore";

const TrackDetails = () => {
  const { id } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Form states
  const [formData, setFormData] = useState({
    // Client Info
    fullname: "",
    email: "",
    phone: "",
    location: "",
    // Shipping form
    time: "",
    shippingLocation: "",
    status: "",
    // Extra details form
    pTime: "",
    pDate: "",
    pComment: "",
    // Product form
    PQuantity: "",
    PType: "",
    PDesc: "",
    PWidth: "",
    PLength: "",
    // Other fields
    percentage: "",
    mapLink: "",
  });

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const clearFormFields = useCallback((fields) => {
    setFormData((prev) => {
      const updated = { ...prev };
      fields.forEach((field) => {
        updated[field] = "";
      });
      return updated;
    });
  }, []);

  useEffect(() => {
    if (!id) return;

    const unsubscribe = onSnapshot(
      doc(db, "tracking", id),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = { ...docSnapshot.data(), id: docSnapshot.id };
          setTrackingData(data);

          setFormData((prev) => ({
            ...prev,
            percentage: data.percentage || "",
            mapLink: data.mapLink || "",
            fullname: data.fullname || "",
            email: data.email || "",
            phone: data.phone || "",
            location: data.location || "",
          }));
        } else {
          setError("Tracking data not found");
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [id]);

  const updateClientInfo = async (e) => {
    e.preventDefault();
    const { fullname, email, phone, location } = formData;
    try {
      const ref = doc(db, "tracking", id);
      await updateDoc(ref, {
        fullname: fullname || "",
        email: email || "",
        phone: phone || "",
        location: location || "",
      });
      alert("Client info updated successfully!");
    } catch (err) {
      setError(err.message);
      alert("Error updating client info");
    }
  };

  const addShipping = async (e) => {
    e.preventDefault();
    const { time, shippingLocation, status } = formData;

    if (!time || !shippingLocation || !status) {
      alert("Please fill in all fields");
      return;
    }

    try {
      const ref = doc(db, "tracking", id);
      await updateDoc(ref, {
        shipping: arrayUnion({ time, location: shippingLocation, status }),
      });

      clearFormFields(["time", "shippingLocation", "status"]);
      alert("Shipping location added successfully!");
    } catch (err) {
      setError(err.message);
      alert("Error adding shipping location");
    }
  };

  const deleteLastItem = async () => {
    if (!trackingData?.shipping?.length) {
      alert("No shipping items to delete");
      return;
    }

    const confirm = window.confirm(
      "Are you sure you want to delete the last shipping item?"
    );

    if (confirm) {
      try {
        const ref = doc(db, "tracking", id);
        const shipping = [...trackingData.shipping];
        shipping.pop();

        await updateDoc(ref, { shipping });
        alert("Last shipping item deleted successfully!");
      } catch (err) {
        setError(err.message);
        alert("Error deleting shipping item");
      }
    }
  };

  const addExtraData = async (e) => {
    e.preventDefault();
    const { pTime, pDate, pComment } = formData;

    if (!pTime || !pDate || !pComment) {
      alert("Please fill in all fields");
      return;
    }

    try {
      const ref = doc(db, "tracking", id);
      await updateDoc(ref, {
        extra: [{ pTime, pDate, pComment }],
      });

      clearFormFields(["pTime", "pDate", "pComment"]);
      alert("Extra details updated successfully!");
    } catch (err) {
      setError(err.message);
      alert("Error updating extra details");
    }
  };

  const addPackage = async (e) => {
    e.preventDefault();
    const { PQuantity, PType, PDesc, PWidth, PLength } = formData;

    if (!PQuantity || !PType || !PDesc || !PWidth || !PLength) {
      alert("Please fill in all fields");
      return;
    }

    try {
      const ref = doc(db, "tracking", id);
      await updateDoc(ref, {
        packaging: arrayUnion({
          PQuantity,
          PType,
          PDesc,
          PWidth,
          PLength,
        }),
      });

      clearFormFields(["PQuantity", "PType", "PDesc", "PWidth", "PLength"]);
      alert("Package added successfully!");
    } catch (err) {
      setError(err.message);
      alert("Error adding package");
    }
  };

  const deleteLastProduct = async () => {
    if (!trackingData?.packaging?.length) {
      alert("No packages to delete");
      return;
    }

    const confirm = window.confirm(
      "Are you sure you want to delete the last package?"
    );

    if (confirm) {
      try {
        const ref = doc(db, "tracking", id);
        const packaging = [...trackingData.packaging];
        packaging.pop();

        await updateDoc(ref, { packaging });
        alert("Last package deleted successfully!");
      } catch (err) {
        setError(err.message);
        alert("Error deleting package");
      }
    }
  };

  const handlePercentage = async (e) => {
    e.preventDefault();
    const { percentage } = formData;

    if (!percentage || percentage < 0 || percentage > 100) {
      alert("Please enter a valid percentage (0-100)");
      return;
    }

    try {
      const ref = doc(db, "tracking", id);
      await updateDoc(ref, { percentage: Number(percentage) });
      alert("Percentage updated successfully!");
    } catch (err) {
      setError(err.message);
      alert("Error updating percentage");
    }
  };

  const handleMapLink = async (e) => {
    e.preventDefault();
    const { mapLink } = formData;

    if (!mapLink) {
      alert("Please enter a map link");
      return;
    }

    try {
      const ref = doc(db, "tracking", id);
      await updateDoc(ref, { mapLink });
      alert("Map link updated successfully!");
    } catch (err) {
      setError(err.message);
      alert("Error updating map link");
    }
  };

  // Styles
  const containerStyle = {
    fontFamily: "system-ui, -apple-system, sans-serif",
    minHeight: "100vh",
    backgroundColor: "#f4f7f6",
    padding: "2rem",
    color: "#333",
  };
  const headerStyle = {
    textAlign: "center",
    marginBottom: "1rem",
    color: "#2c3e50",
    fontSize: "2rem",
  };
  const cardStyle = {
    background: "#fff",
    borderRadius: "12px",
    padding: "2rem",
    maxWidth: "800px",
    margin: "0 auto",
    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
  };
  const sectionTitleStyle = {
    fontSize: "1.2rem",
    fontWeight: "bold",
    borderBottom: "2px solid #ecf0f1",
    paddingBottom: "0.5rem",
    marginBottom: "1.5rem",
    color: "#2980b9",
    marginTop: "2.5rem",
  };
  const inputStyle = {
    width: "100%",
    padding: "0.8rem",
    marginBottom: "1rem",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "1rem",
    boxSizing: "border-box",
  };
  const btnStyle = {
    backgroundColor: "#2980b9",
    color: "#fff",
    padding: "0.8rem 1.5rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: "bold",
    transition: "background 0.3s",
  };
  const deleteBtnStyle = {
    ...btnStyle,
    backgroundColor: "transparent",
    color: "#e74c3c",
    padding: "0",
    textDecoration: "underline",
    marginTop: "0.5rem",
  };
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "1rem",
  };
  const thTdStyle = {
    border: "1px solid #eee",
    padding: "0.8rem",
    textAlign: "left",
  };
  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
  };

  if (loading) {
    return <div style={{ ...containerStyle, display: "flex", justifyContent: "center", alignItems: "center" }}>Loading...</div>;
  }

  if (error || !trackingData) {
    return (
      <div style={containerStyle}>
        <div style={{ color: "red", textAlign: "center", marginBottom: "1rem" }}>{error || "Tracking data not found"}</div>
        <div style={{ textAlign: "center" }}>
          <Link to="/admin-tracking" style={{ color: "#2980b9", textDecoration: "none" }}>&larr; Back to Tracking</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h1 style={headerStyle}>Manage Tracking: {trackingData.id}</h1>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <Link to="/admin-tracking" style={{ color: "#2980b9", textDecoration: "none", fontWeight: "bold" }}>&larr; Back to Tracking</Link>
      </div>

      <div style={cardStyle}>
        
        {/* Client Info Update */}
        <h3 style={{ ...sectionTitleStyle, marginTop: "0" }}>Client Info (Optional)</h3>
        <form onSubmit={updateClientInfo}>
          <div style={gridStyle}>
            <input
              style={inputStyle}
              type="text"
              placeholder="Full Name"
              value={formData.fullname}
              onChange={(e) => handleInputChange("fullname", e.target.value)}
            />
            <input
              style={inputStyle}
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
            <input
              style={inputStyle}
              type="text"
              placeholder="Phone"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
            />
            <input
              style={inputStyle}
              type="text"
              placeholder="Location"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
            />
          </div>
          <button type="submit" style={btnStyle}>Save Client Info</button>
        </form>

        {/* Existing Client Details (if any from older structure) */}
        {trackingData.clientDetails && (
          <div style={{ marginTop: "1.5rem", padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
            <strong>Legacy Client Details:</strong> {trackingData.clientDetails}
          </div>
        )}

        {/* Extra Details */}
        <h3 style={sectionTitleStyle}>Extra Details</h3>
        {trackingData.extra && trackingData.extra.length > 0 ? (
          <div style={{ marginBottom: "1rem" }}>
            {trackingData.extra.map((item, index) => (
              <div key={index} style={{ padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "8px", marginBottom: "0.5rem" }}>
                <p style={{ margin: "0 0 0.5rem" }}><strong>Date:</strong> {item.pDate}</p>
                <p style={{ margin: "0 0 0.5rem" }}><strong>Time:</strong> {item.pTime}</p>
                <p style={{ margin: "0" }}><strong>Comment:</strong> {item.pComment}</p>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: "#7f8c8d" }}>No extra details added yet.</p>
        )}
        <form onSubmit={addExtraData} style={{ marginTop: "1rem" }}>
          <div style={gridStyle}>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>Pickup Date</label>
              <input style={inputStyle} type="date" value={formData.pDate} onChange={(e) => handleInputChange("pDate", e.target.value)} />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>Pickup Time</label>
              <input style={inputStyle} type="time" value={formData.pTime} onChange={(e) => handleInputChange("pTime", e.target.value)} />
            </div>
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>Comment</label>
            <input style={inputStyle} type="text" value={formData.pComment} onChange={(e) => handleInputChange("pComment", e.target.value)} />
          </div>
          <button type="submit" style={btnStyle}>Add Extra Details</button>
        </form>

        {/* Shipping History */}
        <h3 style={sectionTitleStyle}>Shipping Locations</h3>
        <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
          <table style={tableStyle}>
            <thead style={{ backgroundColor: "#ecf0f1" }}>
              <tr>
                <th style={thTdStyle}>Time</th>
                <th style={thTdStyle}>Location</th>
                <th style={thTdStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {trackingData.shipping && trackingData.shipping.length > 0 ? (
                trackingData.shipping.map((item, index) => (
                  <tr key={index}>
                    <td style={thTdStyle}>{item.time}</td>
                    <td style={thTdStyle}>{item.location}</td>
                    <td style={thTdStyle}>{item.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ ...thTdStyle, textAlign: "center", color: "#7f8c8d" }}>No locations added yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <form onSubmit={addShipping} style={gridStyle}>
          <input style={inputStyle} type="datetime-local" value={formData.time} onChange={(e) => handleInputChange("time", e.target.value)} required />
          <input style={inputStyle} type="text" placeholder="Location" value={formData.shippingLocation} onChange={(e) => handleInputChange("shippingLocation", e.target.value)} required />
          <input style={{ ...inputStyle, gridColumn: "span 2" }} type="text" placeholder="Status" value={formData.status} onChange={(e) => handleInputChange("status", e.target.value)} required />
          <button type="submit" style={{ ...btnStyle, gridColumn: "span 2" }}>Add Location</button>
        </form>
        <div style={{ textAlign: "right", marginTop: "0.5rem" }}>
          <button type="button" onClick={deleteLastItem} style={deleteBtnStyle}>Delete Last Location</button>
        </div>

        {/* Packages */}
        <h3 style={sectionTitleStyle}>Product Info</h3>
        <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
          <table style={tableStyle}>
            <thead style={{ backgroundColor: "#ecf0f1" }}>
              <tr>
                <th style={thTdStyle}>Qty</th>
                <th style={thTdStyle}>Type</th>
                <th style={thTdStyle}>Desc</th>
                <th style={thTdStyle}>Length</th>
                <th style={thTdStyle}>Width</th>
              </tr>
            </thead>
            <tbody>
              {trackingData.packaging && trackingData.packaging.length > 0 ? (
                trackingData.packaging.map((item, index) => (
                  <tr key={index}>
                    <td style={thTdStyle}>{item.PQuantity}</td>
                    <td style={thTdStyle}>{item.PType}</td>
                    <td style={thTdStyle}>{item.PDesc}</td>
                    <td style={thTdStyle}>{item.PLength}</td>
                    <td style={thTdStyle}>{item.PWidth}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ ...thTdStyle, textAlign: "center", color: "#7f8c8d" }}>No products added yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <form onSubmit={addPackage}>
          <div style={gridStyle}>
            <input style={inputStyle} type="text" placeholder="Quantity" value={formData.PQuantity} onChange={(e) => handleInputChange("PQuantity", e.target.value)} required />
            <input style={inputStyle} type="text" placeholder="Type" value={formData.PType} onChange={(e) => handleInputChange("PType", e.target.value)} required />
            <input style={inputStyle} type="text" placeholder="Description" value={formData.PDesc} onChange={(e) => handleInputChange("PDesc", e.target.value)} required />
            <input style={inputStyle} type="text" placeholder="Length" value={formData.PLength} onChange={(e) => handleInputChange("PLength", e.target.value)} required />
            <input style={{ ...inputStyle, gridColumn: "span 2" }} type="text" placeholder="Width" value={formData.PWidth} onChange={(e) => handleInputChange("PWidth", e.target.value)} required />
          </div>
          <button type="submit" style={btnStyle}>Add Product</button>
        </form>
        <div style={{ textAlign: "right", marginTop: "0.5rem" }}>
          <button type="button" onClick={deleteLastProduct} style={deleteBtnStyle}>Delete Last Product</button>
        </div>

        {/* Percentage */}
        <h3 style={sectionTitleStyle}>Progress Percentage</h3>
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <strong>Current Progress:</strong>
            <span>{trackingData.percentage || 0}%</span>
          </div>
          <div style={{ width: "100%", height: "20px", backgroundColor: "#ecf0f1", borderRadius: "10px", overflow: "hidden" }}>
            <div style={{ width: `${trackingData.percentage || 0}%`, height: "100%", backgroundColor: "#27ae60", transition: "width 0.3s" }}></div>
          </div>
        </div>
        <form onSubmit={handlePercentage} style={{ display: "flex", gap: "1rem" }}>
          <input style={{ ...inputStyle, marginBottom: 0 }} type="number" min="0" max="100" placeholder="0-100" value={formData.percentage} onChange={(e) => handleInputChange("percentage", e.target.value)} required />
          <button type="submit" style={btnStyle}>Update %</button>
        </form>

        {/* Map Link */}
        <h3 style={sectionTitleStyle}>Map Link</h3>
        {trackingData.mapLink && (
          <div style={{ marginBottom: "1rem" }}>
            <a href={trackingData.mapLink} target="_blank" rel="noopener noreferrer" style={{ color: "#2980b9", textDecoration: "underline" }}>View Current Map</a>
          </div>
        )}
        <form onSubmit={handleMapLink} style={{ display: "flex", gap: "1rem" }}>
          <input style={{ ...inputStyle, marginBottom: 0 }} type="url" placeholder="https://..." value={formData.mapLink} onChange={(e) => handleInputChange("mapLink", e.target.value)} required />
          <button type="submit" style={btnStyle}>Update Map</button>
        </form>

      </div>
    </div>
  );
};

export default TrackDetails;